import {
    Backdrop,
    CircularProgress,
    Typography,
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Paper,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import usePageTitle from "../../../Common/hooks/usePageTitle";
import TopBar from "../../../Layout/components/TopBar/TopBar";
import { useFigmaCleanup } from "../hooks/useFigmaCleanup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import styles from "./OperationsDetailView.module.scss";

const OperationsDetailView = (): JSX.Element => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { selectedReport, isLoading, fetchReportDetail } = useFigmaCleanup();
    const [actionFilter, setActionFilter] = useState<string>("all");
    const [pageSize, setPageSize] = useState<number>(25);

    usePageTitle("Admin | Figma Cleanup Details");

    useEffect(() => {
        if (id) {
            fetchReportDetail(parseInt(id, 10));
        }
    }, [id, fetchReportDetail]);

    const handleFilterChange = (event: SelectChangeEvent) => {
        setActionFilter(event.target.value);
    };

    const getActionColor = (action: string): "default" | "warning" | "error" | "success" | "info" => {
        switch (action) {
            case "Remove":
                return "error";
            case "Warn":
                return "warning";
            case "None":
                return "default";
            case "Error":
                return "error";
            default:
                return "default";
        }
    };

    const columns: GridColDef[] = [
        { field: "email", headerName: "Email", width: 180 },
        { field: "name", headerName: "Name", width: 140 },
        {
            field: "lastSeenFigma",
            headerName: "Last Seen",
            width: 110,
            valueFormatter: (params) => {
                if (!params.value) return "Never";
                return new Date(params.value).toLocaleDateString();
            },
        },
        {
            field: "dateAdded",
            headerName: "Date Added",
            width: 110,
            valueFormatter: (params) => {
                if (!params.value) return "";
                return new Date(params.value).toLocaleDateString();
            },
        },
        { field: "inactiveDays", headerName: "Inactive Days", width: 100, align: "center", headerAlign: "center" },
        {
            field: "action",
            headerName: "Action",
            width: 90,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color={getActionColor(params.value)}
                    size="small"
                />
            ),
        },
        { field: "actionReason", headerName: "Reason", flex: 1, minWidth: 200 },
        {
            field: "success",
            headerName: "Status",
            width: 85,
            renderCell: (params) => (
                <Chip
                    label={params.value ? "Success" : "Failed"}
                    color={params.value ? "success" : "error"}
                    size="small"
                />
            ),
        },
        { field: "errorMessage", headerName: "Error", flex: 1, minWidth: 150 },
    ];

    const filteredDetails = selectedReport?.processingDetails?.filter((detail) => {
        if (actionFilter === "all") return true;
        return detail.action === actionFilter;
    }) ?? [];

    const rows = filteredDetails.map((detail, index) => ({
        id: index,
        ...detail,
    }));

    return (
        <div id="operationsdetail" data-testid="operationsdetail" style={{ height: "100%" }}>
            <TopBar>
                <div className={styles.details__topBarContainer}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate("/admin/operations")}
                        >
                            Back
                        </Button>
                        <Typography variant="h4">
                            Figma Cleanup Report
                        </Typography>
                    </Box>
                </div>
            </TopBar>

            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {selectedReport && (
                <Box pl="80px" pt="24px" height="100%">
                    {/* Summary Section */}
                    <Paper sx={{ width: "92%", p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Report from {new Date(selectedReport.completedDate).toLocaleString()}
                        </Typography>
                        <Box className={styles.summaryGrid}>
                            <Box className={styles.summaryItem}>
                                <Typography className={styles.summaryValue}>
                                    {selectedReport.totalUsersInCsv}
                                </Typography>
                                <Typography className={styles.summaryLabel}>Total Users</Typography>
                            </Box>
                            <Box className={styles.summaryItem}>
                                <Typography className={styles.summaryValue} sx={{ color: "#ed6c02" }}>
                                    {selectedReport.usersWarned}
                                </Typography>
                                <Typography className={styles.summaryLabel}>Warned</Typography>
                            </Box>
                            <Box className={styles.summaryItem}>
                                <Typography className={styles.summaryValue} sx={{ color: "#d32f2f" }}>
                                    {selectedReport.usersRemoved}
                                </Typography>
                                <Typography className={styles.summaryLabel}>Removed</Typography>
                            </Box>
                            <Box className={styles.summaryItem}>
                                <Typography className={styles.summaryValue}>
                                    {selectedReport.skipped}
                                </Typography>
                                <Typography className={styles.summaryLabel}>Skipped</Typography>
                            </Box>
                            <Box className={styles.summaryItem}>
                                <Typography
                                    className={styles.summaryValue}
                                    sx={{ color: selectedReport.errors > 0 ? "#d32f2f" : "#2e7d32" }}
                                >
                                    {selectedReport.errors}
                                </Typography>
                                <Typography className={styles.summaryLabel}>Errors</Typography>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Filter Section */}
                    <Paper sx={{ width: "92%", p: 2, mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Filter by Action</InputLabel>
                            <Select
                                value={actionFilter}
                                label="Filter by Action"
                                onChange={handleFilterChange}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="Warn">Warned</MenuItem>
                                <MenuItem value="Remove">Removed</MenuItem>
                                <MenuItem value="None">Skipped</MenuItem>
                                <MenuItem value="Error">Errors</MenuItem>
                            </Select>
                        </FormControl>
                    </Paper>

                    {/* Details Grid */}
                    <Box sx={{ width: "92%", height: 600, pb: 0, mb: "70px" }}>
                        <Paper sx={{ height: "100%" }}>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                pageSize={pageSize}
                                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                pagination
                                paginationMode="client"
                                disableSelectionOnClick
                            />
                        </Paper>
                    </Box>
                </Box>
            )}
        </div>
    );
};

export default OperationsDetailView;
